import React, {Fragment} from 'react';
import PropTypes from 'prop-types';

import IconChevronRight from '@spothero/icons/chevron-right';
import {
    Divider,
    DrawerBody,
    DrawerContainer,
    DrawerContent,
    DrawerOverlay,
    Flex,
    Heading,
    Icon,
    Link,
    Text,
} from '@spothero/ui';

import Config from 'hooks/config';
import {supportEmail, helpCenterUrl} from 'constants/external-urls';
import {useSegment} from 'hooks/segment';

import SignOutButton from '../common/SignOutButton';
import MobileNavigationHeader from '../mobile-navigation/MobileNavigationHeader';

const {siteUrl} = Config;

const subMenuLinkStyleProps = {
    variant: 'tertiary',
    fontSize: 'sm',
    paddingY: 2,
    paddingX: 3,
    color: 'black',
    display: 'flex',
    alignItems: 'center',
};

const userMenuSectionHeadingStyleProps = {
    fontSize: 'sm',
    color: 'text.secondary.light',
    paddingX: 3.5,
    paddingBottom: 3,
};

const userMenuData = [
    {
        heading: 'Account',
        links: [
            {
                label: 'My Account',
                href: `${siteUrl}/my-account`,
                isExternal: true,
            },
        ],
    },
    {
        heading: 'Enforcement',
        links: [
            {
                label: 'License Plate Lookup',
                href: `${siteUrl}/licenseplate`,
                isExternal: true,
            },
        ],
    },
    {
        heading: 'Get Help',
        links: [
            {
                label: 'FAQs & Guides',
                href: '#',
            },
            {
                label: 'Contact SpotHero',
                subtitle: `Send a message to ${supportEmail}`,
                href: `mailto:${supportEmail}`,
                isExternal: true,
            },
        ],
    },
];

const MobileUserMenu = ({isOpen, handleAccountMenuToggle, onSubMenuToggle}) => {
    const {trackEvent} = useSegment();

    const handleHelpCenterTracking = () => {
        trackEvent({
            event: 'Help Center Clicked',
        });
    };

    return (
        <>
            <Link
                onClick={handleHelpCenterTracking}
                href={helpCenterUrl}
                isExternal
                {...subMenuLinkStyleProps}
            >
                Help Center
                <Icon as={IconChevronRight} boxSize={4} marginLeft={1.5} />
            </Link>
            <Link onClick={handleAccountMenuToggle} {...subMenuLinkStyleProps}>
                Account
                <Icon as={IconChevronRight} boxSize={4} marginLeft={1.5} />
            </Link>
            <DrawerContainer isOpen={isOpen} onClose={onSubMenuToggle}>
                <DrawerOverlay />
                <DrawerContent>
                    <MobileNavigationHeader
                        showBackButton
                        onBackButtonClick={handleAccountMenuToggle}
                    />
                    <DrawerBody>
                        {userMenuData.map((section, index) => {
                            return (
                                <Fragment key={index}>
                                    <Flex direction="column">
                                        <Heading
                                            as="h3"
                                            {...userMenuSectionHeadingStyleProps}
                                        >
                                            {section.heading}
                                        </Heading>
                                        {section.links.map(
                                            (link, linkIndex) => {
                                                const {
                                                    href,
                                                    isExternal,
                                                    label,
                                                    subtitle,
                                                } = link;

                                                return (
                                                    <Link
                                                        as="a"
                                                        key={linkIndex}
                                                        href={href}
                                                        isExternal={isExternal}
                                                        color="black"
                                                        paddingY={3}
                                                        paddingX={3.5}
                                                    >
                                                        <Text>{label}</Text>
                                                        {subtitle && (
                                                            <Text
                                                                fontSize="xs"
                                                                color="text.secondary.light"
                                                            >
                                                                {subtitle}
                                                            </Text>
                                                        )}
                                                    </Link>
                                                );
                                            }
                                        )}
                                    </Flex>
                                    <Divider marginY={4} />
                                </Fragment>
                            );
                        })}
                        <SignOutButton />
                    </DrawerBody>
                </DrawerContent>
            </DrawerContainer>
        </>
    );
};

MobileUserMenu.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    handleAccountMenuToggle: PropTypes.func.isRequired,
    onSubMenuToggle: PropTypes.func.isRequired,
};

export default MobileUserMenu;
